import { useEffect, useMemo, useRef } from 'react';
import { UseFormReturn, FieldValues, Path, PathValue } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUserId } from '@/lib/services/profile';
import { getLawFirmUsersAction } from '@/lib/services/account/actions';
import { gatedAction } from '@/lib/api/auth-gate';

interface UseAutoFillOwnerOptions<
  TFieldValues extends FieldValues = FieldValues
> {
  /**
   * The React Hook Form instance
   */
  form: UseFormReturn<TFieldValues>;

  /**
   * Whether the form is in edit mode (auto-fill disabled in edit mode)
   * @default false
   */
  isEditMode?: boolean;

  /**
   * Whether the form has been initialized
   * Set to true once initial form reset is complete
   */
  formInitialized: boolean;

  /**
   * Whether auto-fill is enabled
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook to automatically fill the 'ownerId' field with the current user's ID
 *
 * This hook:
 * - Fetches the current user ID
 * - Fetches the list of available users
 * - Auto-fills the ownerId field if not already set
 * - Only runs once per form instance
 * - Skips auto-fill in edit mode
 *
 * @example
 * ```tsx
 * function CreateMatterForm({ isEditMode }) {
 *   const form = useForm();
 *   const formInitialized = useRef(false);
 *
 *   // Initialize form
 *   useEffect(() => {
 *     form.reset({ ownerId: '', ... });
 *     formInitialized.current = true;
 *   }, []);
 *
 *   // Auto-fill owner
 *   useAutoFillOwner({
 *     form,
 *     isEditMode,
 *     formInitialized: formInitialized.current
 *   });
 *
 *   return <form>...</form>;
 * }
 * ```
 */
export function useAutoFillOwner<
  TFieldValues extends FieldValues = FieldValues
>({
  form,
  isEditMode = false,
  formInitialized,
  enabled = true
}: UseAutoFillOwnerOptions<TFieldValues>) {
  // Track if we've already auto-filled to prevent multiple runs
  const hasAutoFilledOwnerRef = useRef(false);

  // Fetch current user ID
  const { userId: currentUserId } = useCurrentUserId({
    enabled: enabled && !isEditMode
  });

  // Fetch law firm users. Request a high limit so the current user (and every
  // selectable owner) is present regardless of firm size — the endpoint paginates
  // at 50 by default, which would otherwise drop users beyond page one, breaking
  // both the owner auto-fill and the owner dropdown options in larger firms.
  const { data: usersResponse } = useQuery({
    queryKey: ['law-firm-users', { limit: 200 }],
    queryFn: () => gatedAction(() => getLawFirmUsersAction({ limit: 200 })),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: enabled && !isEditMode
  });

  const lawFirmUsers = useMemo(
    () => usersResponse?.users ?? [],
    [usersResponse?.users]
  );

  // Auto-fill owner with current user ID if not set
  useEffect(() => {
    // Skip if disabled or already auto-filled or in edit mode
    if (!enabled || hasAutoFilledOwnerRef.current || isEditMode) {
      return;
    }

    // Wait for form to be initialized first
    if (!formInitialized) {
      return;
    }

    // Wait for all data to be ready
    const isDataReady =
      currentUserId !== null && usersResponse && lawFirmUsers.length > 0;

    if (!isDataReady) {
      return;
    }

    // Get current owner value
    const ownerIdPath = 'ownerId' as Path<TFieldValues>;
    const currentOwnerId = form.getValues(ownerIdPath);

    // Only auto-fill if owner is not already set
    if (currentOwnerId) {
      hasAutoFilledOwnerRef.current = true;
      return;
    }

    // Convert user ID to string for form field
    const currentUserIdStr = String(currentUserId);

    // Verify the current user is in the users list
    const userExists = lawFirmUsers.find(
      (u) => u.id.toString() === currentUserIdStr
    );

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (userExists) {
      // Set the owner ID - React Hook Form handles the update properly
      timeoutId = setTimeout(() => {
        form.setValue(
          ownerIdPath,
          currentUserIdStr as PathValue<TFieldValues, Path<TFieldValues>>,
          {
            shouldValidate: false,
            shouldDirty: false,
            shouldTouch: false
          }
        );
      });

      hasAutoFilledOwnerRef.current = true;
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    currentUserId,
    usersResponse,
    lawFirmUsers,
    isEditMode,
    formInitialized,
    enabled,
    form
  ]);

  // Return users list for convenience (can be used for dropdowns)
  return {
    lawFirmUsers,
    isLoadingUsers: !usersResponse,
    currentUserId
  };
}
